/**
 * PlatformLogin — halaman login khusus Platform Owner.
 * TIDAK memakai halaman /login (admin/customer).
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { usePlatformAuth } from "@/context/PlatformAuthContext";
import { usePlatformBranding } from "@/context/PlatformBrandingContext";
import { formatApiError, imageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function PlatformLogin() {
  const { user, loading, login } = usePlatformAuth();
  const { platform } = usePlatformBranding();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) nav("/platform", { replace: true });
  }, [user, loading, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Berhasil masuk sebagai Platform Owner");
      nav("/platform", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err) || "Login gagal");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 relative overflow-hidden" style={{ background: "radial-gradient(1200px 600px at 50% 0%, #0D1628, #08111F 70%)" }} data-testid="platform-login-page">
      {/* Ambient gradient blobs */}
      <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full blur-3xl opacity-30" style={{ background: "radial-gradient(circle, #00D4FF 0%, transparent 70%)" }} />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full blur-3xl opacity-30" style={{ background: "radial-gradient(circle, #8A2BE2 0%, transparent 70%)" }} />

      <div className="w-full max-w-md relative">
        <div className="text-center mb-8">
          {platform.platform_logo ? (
            <img src={imageUrl(platform.platform_logo)} alt={platform.platform_name} className="h-16 mx-auto mb-4" onError={(e) => { e.target.src = "/brand/dynicty-mark.svg"; }} />
          ) : (
            <img src="/brand/dynicty-mark.svg" alt="Dynicty" className="h-16 w-16 mx-auto mb-4 drop-shadow-[0_0_30px_rgba(0,212,255,0.4)]" />
          )}
          <div className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color: "#B5C3E0" }}>{platform.platform_tagline || "BUILD • GROW • EMPOWER"}</div>
          <h1 className="font-display text-3xl md:text-4xl font-black mt-3 dynicty-gradient-text">
            {platform.platform_name || "Dynicty"} Owner Portal
          </h1>
          <p className="text-sm text-white/50 mt-2">Kelola seluruh tenant, template, deployment dan infrastruktur dari satu tempat.</p>
        </div>

        <form onSubmit={submit} className="relative rounded-3xl p-6 space-y-4 shadow-2xl dynicty-gradient-border" style={{ backgroundColor: "var(--dynicty-card, #121D33)" }}>
          <div>
            <Label className="text-white/70 text-xs mb-1.5">Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="owner@dynicty.com"
              required
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11 rounded-xl"
              data-testid="platform-login-email"
            />
          </div>
          <div>
            <Label className="text-white/70 text-xs mb-1.5">Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11 rounded-xl"
              data-testid="platform-login-password"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="w-full rounded-xl h-11 font-bold text-white border-0 transition-transform hover:scale-[1.01] active:scale-[0.99] shadow-lg"
            style={{ background: "var(--dynicty-gradient)", boxShadow: "0 8px 30px rgba(0, 212, 255, .25), 0 4px 20px rgba(138, 43, 226, .25)" }}
            data-testid="platform-login-submit"
          >
            {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <KeyRound size={16} className="mr-2" />}
            {busy ? "Memproses..." : `Masuk ke ${platform.platform_name || "Dynicty"}`}
          </Button>

          <p className="text-[11px] text-white/40 text-center pt-2">
            Ini bukan halaman admin toko. Jika Anda admin, silakan buka <a href="/login" className="hover:underline dynicty-gradient-text font-bold">login admin</a>.
          </p>
        </form>

        <div className="text-center mt-6 text-[11px] text-white/30" data-testid="platform-login-copyright">
          {platform.copyright_text || "© Dynicty"} · v{platform.platform_version || "1.0.0"}
        </div>
      </div>
    </div>
  );
}
